import { DatePicker, Select, Spin } from 'antd';
import Button from 'components/common/Button';
import NumericInputField from 'components/common/NumericInputField';
import ShortcutHint from 'components/common/ShortcutHint/ShortcutHint';
import {
  ROTATION_CROP_LOCKED_ACTUAL_MESSAGE,
  ROTATION_CROP_REQUIRED_MESSAGE,
  ROTATION_MULTIPLE_SOWING_MESSAGE,
  ROTATION_RATE_UNIT_REQUIRED_MESSAGE,
  ROTATION_SAVE_CREATES_PLAN_MESSAGE,
  ROTATION_SAVE_UPDATES_PLAN_MESSAGE,
  ROTATION_SEED_MIX_EDIT_NOTE,
  ROTATION_SOWING_DATE_HELP,
  ROTATION_SOWING_DATE_OUT_OF_SEASON_MESSAGE,
  ROTATION_SOWING_DEFAULTS_NOTE,
  ROTATION_SOWING_MISMATCH_EDITOR_MESSAGE,
  ROTATION_SOWING_TASK_MESSAGE,
} from 'constants/messageConstants';
import {
  ROTATION_CANCEL_LABEL,
  ROTATION_CROP_LABEL,
  ROTATION_CROP_PLACEHOLDER,
  ROTATION_EDITOR_TITLE,
  ROTATION_PER_HA_LABEL,
  ROTATION_SAVE_LABEL,
  ROTATION_SEED_COST_LABEL,
  ROTATION_SEED_COST_UNIT,
  ROTATION_SEED_MIX_FIELD_LABEL,
  ROTATION_SOWING_DATE_INPUT_FORMAT,
  ROTATION_SOWING_DATE_LABEL,
  ROTATION_SOWING_PICKER_MONTH_DAY,
  ROTATION_SOWING_RATE_LABEL,
  ROTATION_SOWING_SECTION_LABEL,
  ROTATION_SOWING_UNIT_LABEL,
  ROTATION_VARIETY_LABEL,
  ROTATION_VARIETY_PLACEHOLDER,
  SAVING_STATUS_SAVING_LABEL,
} from 'constants/uiConstants';
import dayjs, { type Dayjs } from 'dayjs';
import { useCropCatalogue } from 'hooks/useCropCatalogue';
import { useFetchGUnits } from 'hooks/useFetchGUnits';
import { useMOrgId } from 'hooks/useMOrgId';
import {
  type GcVariety,
  type GUnit,
  type MergedCrop,
  UnitCategory,
  UnitParameterType,
} from 'interfaces/IGlobalCatalogue';
import type { IUnit, IVariety } from 'interfaces/IInput';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { filterGUnitsByFilter } from 'utils/GUnitFilters';
import { toCalendarDateIso } from 'utils/inputDraftPatches';
import { ADD_RECORD_TITLES, ADD_VARIETY_LABELS } from 'constants/uiConstants';
import type { RootState } from '../../../../../redux/Store';
import TipContent from 'pages/FarmsPage/Workspace/Insights/nutrition/TipContent';
import { rotationSowingKind, type RotationCell } from './computeRotationGrid';
import { formatMixSeed } from './rotationFormat';
import { buildAddedMergedCrop } from '../../Tasks/addRecord';
import AddRecordForm from '../../Tasks/AddRecordForm';
import AddRecordRow from '../../Tasks/AddRecordRow';
import AddVarietyForm from '../../Tasks/AddVarietyForm';
import InlineAddPanel from '../../Tasks/InlineAddPanel';
import type { RotationCellValues } from './useRotationCellSave';

const EMPTY_G_UNITS: GUnit[] = [];

type SowingDefaultField = 'rate' | 'unit' | 'cost';

/** Sowing rate units — the Seed row of the step unit table (stepFactory STEP_UNIT_FILTERS). */
const SEED_RATE_UNIT_FILTER = {
  parameters: [UnitParameterType.SoilInputLargeSolid],
  category: UnitCategory.SoilInput,
};

const POPUP_SCROLL_THRESHOLD_PX = 20;

/** A crop's option value — its catalogue id, or its name for a crop typed in without one. */
const cropKeyOf = (globalId: string | null, name: string) => globalId || name;

interface RotationCellEditorProps {
  paddockName: string;
  season: number;
  /** The plan already in the cell, or null for a new one. */
  cell: RotationCell | null;
  isSaving: boolean;
  /** Why the last save failed, shown at the foot of the form. */
  saveError: string | null;
  onSave: (values: RotationCellValues) => void;
  onCancel: () => void;
}

/**
 * The form a Rotation planner cell opens (OP-2501): crop (required),
 * variety, and optional sowing details. Ctrl/⌘+Enter saves from anywhere in
 * it; Escape cancels. The sowing date must fall in the cell's season, and
 * seed cost is per unit of the sowing rate, as in the Tasks grid.
 */
const RotationCellEditor: React.FC<RotationCellEditorProps> = ({
  paddockName,
  season,
  cell,
  isSaving,
  saveError,
  onSave,
  onCancel,
}) => {
  const gOrgId =
    useSelector(
      (state: RootState) => state.globalCatalogueReducer.activeGOrganisation
    ) || null;
  const mOrgId = useMOrgId();
  const { data: allUnits = EMPTY_G_UNITS } = useFetchGUnits(gOrgId);
  const rateUnits = useMemo(
    () => filterGUnitsByFilter(allUnits, SEED_RATE_UNIT_FILTER),
    [allUnits]
  );

  const [crop, setCrop] = useState<{
    cropName: string;
    globalId: string | null;
  } | null>(
    cell?.cropName ? { cropName: cell.cropName, globalId: cell.cropId } : null
  );
  const [variety, setVariety] = useState<IVariety | null>(
    cell?.varietyName
      ? { varietyName: cell.varietyName, globalId: cell.varietyId }
      : null
  );
  const sowingLocked = cell?.sowingState === 'multiple';
  // A seed mix keeps its seeds here (they're changed in Planning); only the
  // sowing date can be set, and a save writes the mix back as it is.
  const mix = sowingLocked ? null : (cell?.sowing?.mix ?? null);
  // A plan with an Actual sowing record keeps its crop and variety here, as
  // a paste does: changing them would rewrite what was actually sown.
  const cropLocked = !!cell?.hasActualSowing;
  const [rate, setRate] = useState<number | null>(cell?.sowing?.rate ?? null);
  const [unit, setUnit] = useState<IUnit | null>(cell?.sowing?.unit ?? null);
  const [cost, setCost] = useState<number | null>(cell?.sowing?.cost ?? null);
  // Sowing fields filled from the crop's catalogue defaults and not edited
  // since — the ones a different crop may replace with its own defaults.
  const [defaulted, setDefaulted] = useState<Set<SowingDefaultField>>(
    () => new Set()
  );
  const markEdited = (field: SowingDefaultField) =>
    setDefaulted((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  const [date, setDate] = useState<string | null>(
    cell?.sowing?.dateHasDay ? (cell.sowing.date ?? null) : null
  );
  const [showErrors, setShowErrors] = useState(false);
  // The inline Add form under the crop / variety fields (OP-2498).
  const [addPanel, setAddPanel] = useState<{
    kind: 'crop' | 'variety';
    name: string;
  } | null>(null);
  const [varietySearch, setVarietySearch] = useState('');
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [isVarietyOpen, setIsVarietyOpen] = useState(false);

  const {
    items: browseCrops,
    allMergedItems,
    searchItems,
    isLoading: isCropsLoading,
    isPaginating,
    hasNextPage,
    fetchNextPage,
    searchTerm,
    onSearch,
    clearSearch,
  } = useCropCatalogue(gOrgId, mOrgId, crop?.globalId ?? null);

  const findCrop = (key: string): MergedCrop | undefined =>
    [...allMergedItems, ...searchItems].find(
      (option) => cropKeyOf(option.globalId, option.name) === key
    );
  const selectedCrop = crop
    ? findCrop(cropKeyOf(crop.globalId, crop.cropName))
    : undefined;

  const cropOptions = useMemo(() => {
    const options = browseCrops.map((option) => ({
      value: cropKeyOf(option.globalId, option.name),
      label: option.name,
    }));
    // Keep the plan's crop listed even before the browse list reaches it.
    if (crop) {
      const key = cropKeyOf(crop.globalId, crop.cropName);
      if (!options.some((option) => option.value === key)) {
        options.unshift({ value: key, label: crop.cropName });
      }
    }
    return options;
  }, [browseCrops, crop]);

  const varietyOptions = useMemo(() => {
    const options = (selectedCrop?.varieties ?? []).map((option) => ({
      value: cropKeyOf(option.varietyId, option.varietyName),
      label: option.varietyName,
    }));
    if (variety) {
      const key = cropKeyOf(variety.globalId, variety.varietyName);
      if (!options.some((option) => option.value === key)) {
        options.unshift({ value: key, label: variety.varietyName });
      }
    }
    return options;
  }, [selectedCrop, variety]);

  const rateUnitOptions = useMemo(() => {
    const options = rateUnits.map((option) => ({
      value: option.globalId,
      label: option.code,
    }));
    if (unit && !options.some((option) => option.value === unit.globalId)) {
      options.unshift({
        value: unit.globalId ?? unit.unit,
        label: unit.unit,
      });
    }
    return options;
  }, [rateUnits, unit]);

  /**
   * Fills the sowing rate, unit and seed cost from the crop's catalogue
   * defaults, as picking a seed in the Tasks grid does. Only empty fields,
   * or ones filled from the previous crop's defaults, are filled — anything
   * the advisor typed is kept.
   */
  const applyCropDefaults = (option: MergedCrop) => {
    if (sowingLocked || mix) return;
    const next = new Set(defaulted);
    const fill = <T,>(
      field: SowingDefaultField,
      current: T | null,
      value: T | null | undefined,
      set: (value: T | null) => void
    ) => {
      if (current != null && !defaulted.has(field)) return;
      set(value ?? null);
      if (value != null) next.add(field);
      else next.delete(field);
    };
    fill('rate', rate, option.rate, setRate);
    fill('unit', unit, option.rateUnit, setUnit);
    fill('cost', cost, option.cost, setCost);
    setDefaulted(next);
  };

  const handleCropChange = (key: string) => {
    const option = findCrop(key);
    if (!option) return;
    setCrop({ cropName: option.name, globalId: option.globalId });
    setVariety(null);
    applyCropDefaults(option);
    clearSearch();
  };

  const handleVarietyChange = (key: string | undefined) => {
    if (!key) {
      setVariety(null);
      return;
    }
    const option = selectedCrop?.varieties.find(
      (v) => cropKeyOf(v.varietyId, v.varietyName) === key
    );
    if (option) {
      setVariety({
        varietyName: option.varietyName,
        globalId: option.varietyId,
      });
    }
  };

  // A crop just added from the crop field: selected with its catalogue
  // defaults, as picking one is.
  const handleCropAdded = (added: MergedCrop) => {
    setAddPanel(null);
    setCrop({ cropName: added.name, globalId: added.globalId });
    setVariety(null);
    applyCropDefaults(added);
    clearSearch();
  };

  // A variety just added to the selected crop.
  const handleVarietyAdded = (added: GcVariety) => {
    setAddPanel(null);
    setVariety({ varietyName: added.varietyName, globalId: added.varietyId });
  };

  const handleUnitChange = (key: string) => {
    const option = rateUnits.find((u) => u.globalId === key);
    if (!option) return;
    setUnit({ unit: option.code, globalId: option.globalId });
    markEdited('unit');
  };

  const dateValue = date ? dayjs(date.slice(0, 10)) : null;
  const dateInSeason = !dateValue || dateValue.year() === season;
  const errors = {
    crop: !crop ? ROTATION_CROP_REQUIRED_MESSAGE : null,
    unit:
      !sowingLocked && !mix && rate != null && !unit
        ? ROTATION_RATE_UNIT_REQUIRED_MESSAGE
        : null,
    date:
      !sowingLocked && !dateInSeason
        ? ROTATION_SOWING_DATE_OUT_OF_SEASON_MESSAGE(season)
        : null,
  };
  const isValid = !errors.crop && !errors.unit && !errors.date;

  const save = () => {
    if (isSaving) return;
    setShowErrors(true);
    if (!isValid || !crop) return;
    onSave({
      crop,
      variety,
      sowing: mix
        ? { rate: null, unit: null, cost: null, date, mix }
        : { rate, unit, cost, date, mix: null },
    });
  };

  const formRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Start on the crop, the one field every cell needs (or the first field
    // that can be changed, when the crop is locked).
    formRef.current
      ?.querySelector<HTMLInputElement>('input:not([disabled])')
      ?.focus();
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      save();
      return;
    }
    // An open dropdown takes Escape first, to close itself.
    if (
      event.key === 'Escape' &&
      !formRef.current?.querySelector('.ant-select-open, .ant-picker-focused')
    ) {
      event.preventDefault();
      onCancel();
    }
  };

  const fieldError = (message: string | null) =>
    showErrors && message ? (
      <p className="mt-1 text-sm text-danger-dark">{message}</p>
    ) : null;

  return (
    <div
      ref={formRef}
      role="dialog"
      aria-label={ROTATION_EDITOR_TITLE(paddockName, season)}
      className="flex w-[25rem] max-w-[calc(100vw-2rem)] flex-col gap-3"
      onKeyDown={handleKeyDown}
    >
      <p className="text-base font-semibold text-charcoal">
        {ROTATION_EDITOR_TITLE(paddockName, season)}
      </p>

      {cropLocked && (
        <p role="note" className="text-sm text-gray-dark">
          {ROTATION_CROP_LOCKED_ACTUAL_MESSAGE}
        </p>
      )}

      <div className="grid grid-cols-[7.5rem_1fr] items-start gap-x-3 gap-y-2">
        <label htmlFor="rotation-crop" className="pt-1 text-sm text-gray-dark">
          {ROTATION_CROP_LABEL} <span className="text-danger-dark">*</span>
        </label>
        <div className="min-w-0">
          <Select
            id="rotation-crop"
            disabled={cropLocked}
            showSearch
            filterOption={false}
            className="w-full"
            placeholder={ROTATION_CROP_PLACEHOLDER}
            value={crop ? cropKeyOf(crop.globalId, crop.cropName) : undefined}
            options={cropOptions}
            searchValue={searchTerm}
            onSearch={onSearch}
            onChange={handleCropChange}
            loading={isCropsLoading}
            status={showErrors && errors.crop ? 'error' : undefined}
            open={isCropOpen}
            onOpenChange={setIsCropOpen}
            notFoundContent={
              isCropsLoading ? (
                <Spin size="small" />
              ) : searchTerm.trim() ? (
                <AddRecordRow
                  activateOnMouseDown
                  searchText={searchTerm}
                  onClick={() => {
                    setIsCropOpen(false);
                    setAddPanel({ kind: 'crop', name: searchTerm });
                  }}
                />
              ) : undefined
            }
            onPopupScroll={(event) => {
              const target = event.currentTarget;
              if (
                target.scrollTop + target.clientHeight >=
                  target.scrollHeight - POPUP_SCROLL_THRESHOLD_PX &&
                hasNextPage &&
                !isPaginating
              ) {
                fetchNextPage();
              }
            }}
          />
          {fieldError(errors.crop)}
        </div>

        <label
          htmlFor="rotation-variety"
          className="pt-1 text-sm text-gray-dark"
        >
          {ROTATION_VARIETY_LABEL}
        </label>
        <Select
          id="rotation-variety"
          allowClear
          showSearch
          optionFilterProp="label"
          className="w-full"
          placeholder={ROTATION_VARIETY_PLACEHOLDER}
          disabled={!crop || cropLocked}
          value={
            variety
              ? cropKeyOf(variety.globalId, variety.varietyName)
              : undefined
          }
          options={varietyOptions}
          onChange={handleVarietyChange}
          open={isVarietyOpen}
          onOpenChange={(open) => {
            setIsVarietyOpen(open);
            if (!open) setVarietySearch('');
          }}
          onSearch={setVarietySearch}
          notFoundContent={
            varietySearch.trim() ? (
              <AddRecordRow
                  activateOnMouseDown
                searchText={varietySearch}
                onClick={() => {
                  const name = varietySearch;
                  setIsVarietyOpen(false);
                  setVarietySearch('');
                  setAddPanel({ kind: 'variety', name });
                }}
              />
            ) : undefined
          }
        />

        {addPanel?.kind === 'crop' && (
          <div className="col-span-2">
            <InlineAddPanel
              title={ADD_RECORD_TITLES.crop}
              onClose={() => setAddPanel(null)}
            >
              <AddRecordForm
                kind="crop"
                initialName={addPanel.name}
                gUnits={allUnits}
                onCreated={(option, values) => {
                  if (option.globalId) {
                    handleCropAdded(
                      buildAddedMergedCrop(option.globalId, values)
                    );
                  }
                }}
              />
            </InlineAddPanel>
          </div>
        )}
        {addPanel?.kind === 'variety' && crop && (
          <div className="col-span-2">
            <InlineAddPanel
              title={`${ADD_VARIETY_LABELS.title}: ${crop.cropName}`}
              onClose={() => setAddPanel(null)}
            >
              <AddVarietyForm
                crop={{
                  label: crop.cropName,
                  item: crop.cropName,
                  globalId: crop.globalId,
                  varieties: selectedCrop?.varieties ?? [],
                }}
                initialName={addPanel.name}
                onCreated={(_selection, added) => handleVarietyAdded(added)}
              />
            </InlineAddPanel>
          </div>
        )}
      </div>

      <div className="border-t border-gray-bright pt-2">
        <p className="mb-2 text-sm font-semibold text-gray-dark">
          {ROTATION_SOWING_SECTION_LABEL}
        </p>
        {defaulted.size > 0 && (
          <p className="mb-2 text-sm text-gray-dark">
            {ROTATION_SOWING_DEFAULTS_NOTE}
          </p>
        )}
        {cell?.sowingMismatch && (
          <p role="note" className="mb-2 text-sm text-danger-dark">
            {ROTATION_SOWING_MISMATCH_EDITOR_MESSAGE(
              cell.sowingMismatch.cropName,
              cell.sowingMismatch.varietyName,
              rotationSowingKind(cell)
            )}
          </p>
        )}
        {sowingLocked ? (
          <div className="rounded bg-secondary-light p-2 text-sm text-gray-dark">
            <TipContent tip={ROTATION_MULTIPLE_SOWING_MESSAGE} />
          </div>
        ) : (
          <div className="grid grid-cols-[7.5rem_1fr] items-start gap-x-3 gap-y-2">
            {mix ? (
              <>
                <span className="text-sm text-gray-dark">
                  {ROTATION_SEED_MIX_FIELD_LABEL}
                </span>
                <div className="min-w-0 text-sm text-charcoal">
                  <ul>
                    {mix.map((seed, index) => (
                      <li
                        // A mix can list the same seed twice, so the
                        // position keeps the key unique.
                        key={`${seed.cropId ?? seed.cropName}:${seed.varietyId ?? seed.varietyName ?? ''}:${index}`}
                      >
                        {formatMixSeed(seed)}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1 text-gray-muted">
                    {ROTATION_SEED_MIX_EDIT_NOTE}
                  </p>
                </div>
              </>
            ) : (
              <>
                <label
                  htmlFor="rotation-rate"
                  className="pt-7 text-sm text-gray-dark"
                >
                  {ROTATION_SOWING_RATE_LABEL}
                </label>
                <div className="min-w-0">
                  {/* The unit is a weight, per hectare as labelled above it. */}
                  <div className="flex items-end gap-2">
                    <NumericInputField
                      id="rotation-rate"
                      className="min-w-0 flex-1"
                      inputClassName="w-full"
                      size="middle"
                      min={0}
                      value={rate}
                      onChange={(value) => {
                        setRate(value);
                        markEdited('rate');
                      }}
                    />
                    <span className="flex w-24 flex-shrink-0 flex-col">
                      <span className="mb-0.5 text-sm text-gray-muted">
                        {ROTATION_PER_HA_LABEL}
                      </span>
                      <Select
                        aria-label={`${ROTATION_SOWING_UNIT_LABEL} ${ROTATION_PER_HA_LABEL}`}
                        className="w-full"
                        value={unit ? (unit.globalId ?? unit.unit) : undefined}
                        options={rateUnitOptions}
                        placeholder={ROTATION_SOWING_UNIT_LABEL}
                        onChange={handleUnitChange}
                        status={showErrors && errors.unit ? 'error' : undefined}
                      />
                    </span>
                  </div>
                  {fieldError(errors.unit)}
                </div>
              </>
            )}

            <label
              htmlFor="rotation-date"
              className="pt-1 text-sm text-gray-dark"
            >
              {ROTATION_SOWING_DATE_LABEL}
            </label>
            <div className="min-w-0">
              <DatePicker
                id="rotation-date"
                className="w-full"
                format={ROTATION_SOWING_DATE_INPUT_FORMAT}
                value={dateValue}
                defaultPickerValue={dayjs(
                  `${season}-${ROTATION_SOWING_PICKER_MONTH_DAY}`
                )}
                disabledDate={(day: Dayjs) => day.year() !== season}
                onChange={(day: Dayjs | null) =>
                  setDate(day ? toCalendarDateIso(day.toDate()) : null)
                }
                status={showErrors && errors.date ? 'error' : undefined}
              />
              {fieldError(errors.date)}
              <p className="mt-1 text-sm text-gray-muted">
                {ROTATION_SOWING_DATE_HELP}
              </p>
            </div>

            {!mix && (
              <>
                <label
                  htmlFor="rotation-cost"
                  className="pt-1 text-sm text-gray-dark"
                >
                  {ROTATION_SEED_COST_LABEL}
                </label>
                <NumericInputField
                  id="rotation-cost"
                  inputClassName="w-full"
                  className="min-w-0"
                  size="middle"
                  min={0}
                  value={cost}
                  unit={ROTATION_SEED_COST_UNIT(unit?.unit ?? null)}
                  onChange={(value) => {
                    setCost(value);
                    markEdited('cost');
                  }}
                />
              </>
            )}
          </div>
        )}
      </div>

      <div className="text-sm text-gray-dark">
        <p>
          {cell
            ? ROTATION_SAVE_UPDATES_PLAN_MESSAGE
            : ROTATION_SAVE_CREATES_PLAN_MESSAGE}
        </p>
        {!sowingLocked && <p>{ROTATION_SOWING_TASK_MESSAGE}</p>}
      </div>

      {saveError && (
        <p role="alert" className="text-sm text-danger-dark">
          {saveError}
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        <span className="mr-auto text-gray-muted">
          <ShortcutHint shortcutKey="Enter" variant="subtle" />
        </span>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="px-3"
          onClick={onCancel}
        >
          {ROTATION_CANCEL_LABEL}
        </Button>
        <Button
          type="button"
          variant="success"
          size="sm"
          className="px-3"
          disabled={isSaving}
          onClick={save}
        >
          {isSaving ? SAVING_STATUS_SAVING_LABEL : ROTATION_SAVE_LABEL}
        </Button>
      </div>
    </div>
  );
};

export default RotationCellEditor;
